import { CoreIAMProvider, useAuth } from '@coreiam/auth/react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { AdminRole } from '../domain';
import { type MeResponse, meResponseSchema } from '../validation';
import { ApiRequestError, apiFetch } from './apiClient';

/**
 * Auth state for both frontends.
 *
 * `CoreIAMProvider` establishes *who* the user is (it calls the API's
 * `/api/auth/session`). `/api/me` then answers *what they may do* — the donor
 * row and our own admin roles. Authorisation always comes from our API, never
 * from a role claim in the token.
 */

export interface AuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  me: MeResponse | null;
  error: Error | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/** Must match the API's AUTH_PATH. */
export const SESSION_URL = '/api/auth/session';

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <CoreIAMProvider sessionUrl={SESSION_URL}>
      <AuthResolver>{children}</AuthResolver>
    </CoreIAMProvider>
  );
}

function AuthResolver({ children }: { children: ReactNode }) {
  const { user, isLoading: sessionLoading } = useAuth();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loadingMe, setLoadingMe] = useState(false);

  const sub = user?.sub;

  const load = useCallback(async () => {
    if (!sub) {
      setMe(null);
      return;
    }
    setLoadingMe(true);
    setError(null);
    try {
      const payload = await apiFetch<unknown>('/api/me');
      setMe(meResponseSchema.parse(payload));
    } catch (cause) {
      setMe(null);
      setError(cause instanceof Error ? cause : new Error('failed to load profile'));
    } finally {
      setLoadingMe(false);
    }
  }, [sub]);

  useEffect(() => {
    void load();
  }, [load]);

  const signOut = useCallback(async () => {
    await apiFetch('/api/auth/logout', { method: 'POST' });
    setMe(null);
    window.location.assign('/');
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      isLoading: sessionLoading || loadingMe,
      isAuthenticated: Boolean(sub) && me !== null,
      me,
      error,
      refresh: load,
      signOut,
    }),
    [sessionLoading, loadingMe, sub, me, error, load, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthState(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthState must be used inside <AuthProvider>');
  }
  return context;
}

/** True when the signed-in user holds at least one of the given admin roles. */
export function hasAdminRole(me: MeResponse | null, ...roles: AdminRole[]): boolean {
  return Boolean(me?.adminRoles.some((role) => roles.includes(role)));
}

export { ApiRequestError };
